// auth.js - Utility functions for authentication
import { createStore } from 'redux';

// Mock database - In production, replace with real API calls
let users = JSON.parse(localStorage.getItem("teamhub_users") || "[]");
let currentUser = JSON.parse(sessionStorage.getItem("current_user") || "null");

// Initial state for Redux store
const initialState = {
    user: currentUser,
    isAuthenticated: !!currentUser,
};

// Reducer function
const authReducer = (state = initialState, action) => {
    switch (action.type) {
        case "LOGIN_SUCCESS":
            return { ...state, user: action.payload, isAuthenticated: true };
        case "LOGOUT":
            return { ...state, user: null, isAuthenticated: false };
        case "UPDATE_USER":
            return { ...state, user: { ...state.user, ...action.payload } };
        default:
            return state;
    }
};

// Create Redux store
export const authStore = createStore(authReducer);

/**
 * Save user to localStorage (mock database)
 * @param {Object} user - User object containing email, password, role
 * @returns {boolean} - Success status
 */
export const saveUser = (user) => {
    try {
        // Check if user already exists
        const existingUser = users.find(u => u.email === user.email);
        if (existingUser) {
            return false;
        }

        // Add new user
        const newUser = {
            ...user,
            id: Date.now().toString(),
            createdAt: new Date().toISOString(),
            isActive: true,
            lastLogin: null,
        };

        users.push(newUser);
        localStorage.setItem("teamhub_users", JSON.stringify(users));
        return true;
    } catch (error) {
        console.error("Error saving user:", error);
        return false;
    }
};

/**
 * Authenticate user login
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {boolean} - Authentication success
 */
export const loginUser = (email, password) => {
    try {
        const user = users.find(u =>
            u.email === email &&
            u.password === password &&
            u.isActive !== false
        );

        if (user) {
            // Update last login
            user.lastLogin = new Date().toISOString();
            localStorage.setItem("teamhub_users", JSON.stringify(users));

            // Store in sessionStorage for persistence
            const { password: _, ...userWithoutPassword } = user;
            sessionStorage.setItem("current_user", JSON.stringify(userWithoutPassword));

            // Update Redux store
            authStore.dispatch({ type: "LOGIN_SUCCESS", payload: userWithoutPassword });

            return true;
        }
        return false;
    } catch (error) {
        console.error("Login error:", error);
        return false;
    }
};

/**
 * Logout current user
 */
export const logoutUser = () => {
    try {
        sessionStorage.removeItem("current_user");
        authStore.dispatch({ type: "LOGOUT" });
        return true;
    } catch (error) {
        console.error("Logout error:", error);
        return false;
    }
};

/**
 * Get current logged in user
 * @returns {Object|null} - Current user object or null
 */
export const getCurrentUser = () => {
    try {
        const user = JSON.parse(sessionStorage.getItem("current_user") || "null");
        return user;
    } catch (error) {
        console.error("Error getting current user:", error);
        return null;
    }
};

/**
 * Check if user is authenticated
 * @returns {boolean} - Authentication status
 */
export const isAuthenticated = () => {
    return !!getCurrentUser();
};

/**
 * Update current user profile
 * @param {Object} updates - User profile updates
 * @returns {boolean} - Success status
 */
export const updateUserProfile = (updates) => {
    try {
        const currentUser = getCurrentUser();
        if (!currentUser) return false;

        // Update in users array
        const userIndex = users.findIndex(u => u.email === currentUser.email);
        if (userIndex === -1) return false;

        users[userIndex] = { ...users[userIndex], ...updates };
        localStorage.setItem("teamhub_users", JSON.stringify(users));

        // Update session storage
        const updatedUser = { ...currentUser, ...updates };
        sessionStorage.setItem("current_user", JSON.stringify(updatedUser));

        // Update Redux store
        authStore.dispatch({ type: "UPDATE_USER", payload: updates });

        return true;
    } catch (error) {
        console.error("Error updating user profile:", error);
        return false;
    }
};

/**
 * Get all users (admin only)
 * @returns {Array} - Array of all users
 */
export const getAllUsers = () => {
    try {
        return users.map(({ password, ...user }) => user);
    } catch (error) {
        console.error("Error getting all users:", error);
        return [];
    }
};

/**
 * Update user role (admin only)
 * @param {string} userId - User ID
 * @param {string} newRole - New role
 * @returns {boolean} - Success status
 */
export const updateUserRole = (userId, newRole) => {
    try {
        const userIndex = users.findIndex(u => u.id === userId);
        if (userIndex === -1) return false;

        users[userIndex].role = newRole;
        users[userIndex].updatedAt = new Date().toISOString();
        localStorage.setItem("teamhub_users", JSON.stringify(users));

        // If updating current user, update session storage
        const currentUser = getCurrentUser();
        if (currentUser && currentUser.id === userId) {
            currentUser.role = newRole;
            sessionStorage.setItem("current_user", JSON.stringify(currentUser));
            authStore.dispatch({ type: "UPDATE_USER", payload: { role: newRole } });
        }

        return true;
    } catch (error) {
        console.error("Error updating user role:", error);
        return false;
    }
};

/**
 * Delete user (admin only)
 * @param {string} userId - User ID
 * @returns {boolean} - Success status
 */
export const deleteUser = (userId) => {
    try {
        const initialLength = users.length;
        users = users.filter(u => u.id !== userId);

        if (users.length === initialLength) return false;

        localStorage.setItem("teamhub_users", JSON.stringify(users));
        return true;
    } catch (error) {
        console.error("Error deleting user:", error);
        return false;
    }
};

/**
 * Check if user has specific role
 * @param {string} role - Role to check
 * @returns {boolean} - Role match status
 */
export const hasRole = (role) => {
    const user = getCurrentUser();
    return user?.role === role;
};

/**
 * Check if user has any of the specified roles
 * @param {Array} roles - Array of roles
 * @returns {boolean} - Role match status
 */
export const hasAnyRole = (roles) => {
    const user = getCurrentUser();
    return user && roles.includes(user.role);
};

/**
 * Initialize mock users if none exist
 */
export const initializeMockUsers = () => {
    if (users.length === 0) {
        const mockUsers = [
            {
                id: "1",
                email: "admin@teamhub.com",
                password: "admin123",
                role: "Admin",
                name: "System Administrator",
                createdAt: new Date().toISOString(),
                isActive: true
            },
            {
                id: "2",
                email: "leader@teamhub.com",
                password: "leader123",
                role: "ProjectLeader",
                name: "Project Manager",
                createdAt: new Date().toISOString(),
                isActive: true
            },
            {
                id: "3",
                email: "member@teamhub.com",
                password: "member123",
                role: "TeamMember",
                name: "Team Member",
                createdAt: new Date().toISOString(),
                isActive: true
            }
        ];

        users = mockUsers;
        localStorage.setItem("teamhub_users", JSON.stringify(users));
    }
};

// Initialize mock users on module load
initializeMockUsers();

// Export the Redux store for use in components
export default authStore;